import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fetchAddProjectSupplementMaterials } from '@/service/api/project';
import ImageUploadBox from '@/components/common/ImageUploadBox';

interface AddSupplementMaterialsModalProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export default function AddSupplementMaterialsModal({
  open,
  projectId,
  onClose,
  onSuccess,
  onError,
}: AddSupplementMaterialsModalProps) {
  const [attachmentsInput, setAttachmentsInput] = useState<
    { display_name: string; upload_asset_id: string; sort_no: number }[]
  >([{ display_name: '', upload_asset_id: '', sort_no: 1 }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAttachmentsInput([{ display_name: '', upload_asset_id: '', sort_no: 1 }]);
    }
  }, [open]);

  if (!open) return null;

  /** 提交新增补充资料 */
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validAttachments = attachmentsInput.filter((a) => a.upload_asset_id.trim());
    if (validAttachments.length === 0) {
      onError('请至少上传一份补充资料附件');
      return;
    }
    setIsSubmitting(true);
    try {
      await fetchAddProjectSupplementMaterials(projectId, {
        attachments: validAttachments,
      });
      onSuccess(`成功添加 ${validAttachments.length} 份补充资料！`);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : '上传补充资料失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadChange = (idx: number, assetId: string, file?: File) => {
    setAttachmentsInput((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        let defaultName = item.display_name;
        // 如果未填写显示名称且提供了文件，自动读取文件名
        if (!defaultName.trim() && file?.name) {
          defaultName = file.name;
        }
        return {
          ...item,
          upload_asset_id: assetId,
          display_name: defaultName,
        };
      })
    );
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form
        onSubmit={handleAddSubmit}
        className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150"
      >
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
          <h4 className="font-bold text-foreground-900 text-base">上传/提交项目补充资料</h4>
          <button
            type="button"
            onClick={onClose}
            className="text-foreground-400 hover:text-foreground-700 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <p className="text-xs text-foreground-500 mb-4">你可以批量选择或拖拽上传补充资料文件，系统将自动生成对应的资源记录</p>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 mb-4">
          {attachmentsInput.map((row, idx) => (
            <div
              key={idx}
              className="p-4 rounded-xl bg-gray-50/70 border border-gray-200/80 space-y-3 relative group"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground-700">
                  附件清单 #{idx + 1}
                </span>
                {attachmentsInput.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setAttachmentsInput((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-xs text-rose-500 hover:text-rose-700 flex items-center gap-0.5 transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i> 删除此项
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                <div className="sm:col-span-5">
                  <label className="block text-[11px] font-medium text-foreground-600 mb-1">
                    文件显示名称 (display_name)
                  </label>
                  <input
                    type="text"
                    value={row.display_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAttachmentsInput((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, display_name: val } : item))
                      );
                    }}
                    placeholder="如：补充授权协议扫描件"
                    className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 focus:border-primary-500 outline-none transition-colors"
                  />
                </div>

                <div className="sm:col-span-7">
                  <ImageUploadBox
                    label="资料附件文件"
                    required
                    value={row.upload_asset_id}
                    onChange={(assetId, file) => handleUploadChange(idx, assetId, file)}
                    scene="APPLICATION"
                    policy="uploader-only"
                    accept="*/*"
                    placeholder="点击或拖拽上传补充资料文件"
                    subPlaceholder="支持图片、PDF、Word、压缩包等常见格式"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() =>
            setAttachmentsInput((prev) => [
              ...prev,
              { display_name: '', upload_asset_id: '', sort_no: prev.length + 1 },
            ])
          }
          className="w-full py-2.5 rounded-xl border border-dashed border-primary-300 text-primary-700 text-xs font-medium hover:bg-primary-50/50 mb-4 flex items-center justify-center gap-1 transition-colors cursor-pointer"
        >
          <i className="ri-add-line text-sm"></i> 增加一份补充附件
        </button>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-100 text-foreground-700 text-xs hover:bg-gray-200 transition-colors"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isSubmitting || attachmentsInput.every((i) => !i.upload_asset_id.trim())}
            className="px-5 py-2 rounded-xl bg-primary-500 text-foreground-950 font-medium text-xs hover:bg-primary-400 disabled:opacity-50 flex items-center gap-1 transition-colors"
          >
            {isSubmitting && <i className="ri-loader-4-line animate-spin"></i>}
            提交补充资料
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
